// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
  Icon,
} from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../common/components';
import { DateUtils } from '../../../../../common/utils';
import { useDialog } from '../../../../../common/hooks';
import CampaignSeriesForm from '../../common/CampaignSeriesForm';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '1200px',
    overflow: 'hidden',
  },
  checkbox: {
    flex: '1',
    justifyContent: 'flex-start',
    pointerEvents: 'none',
  },
  actions: {
    display: 'flex',
    '& > .MuiButton-root': {
      marginRight: theme.spacing(1),
      '&:last-child': {
        marginRight: '0',
      },
    },
  },
}));

function CampaignSeriesTab(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    campaignId,
    series,
    seriesSegments,
    showActive,
    actionsDisabled,
    onCreateSeries,
    onUpdateSeries,
    onFilterChange,
    onDownloadVouchers,
  } = props;

  const handleShowCreateSeriesDialog = () => {
    showDialog({
      title: t('campaign.campaignSeriesCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <CampaignSeriesForm
            campaignId={campaignId}
            seriesSegments={seriesSegments}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
          />
        </Box>
      ),
      onConfirm: onCreateSeries,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const handleShowUpdateSeriesDialog = seriesData => {
    showDialog({
      title: t('campaign.campaignSeriesUpdate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <CampaignSeriesForm
            campaignId={campaignId}
            series={seriesData}
            seriesSegments={seriesSegments}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            editMode
          />
        </Box>
      ),
      onConfirm: onUpdateSeries,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const getColumnDefs = () => [
    {
      field: 'title',
      headerName: t('campaign.campaignSeriesTitle'),
      width: 200,
    },
    {
      field: 'segment',
      headerName: t('campaign.campaignSeriesSegmentTitle'),
      width: 175,
      renderCell: params => <span>{params.value?.title}</span>,
    },
    {
      field: 'prefix',
      headerName: t('campaign.campaignSeriesPrefix'),
      width: 125,
    },
    {
      field: 'voucherCount',
      headerName: t('campaign.campaignSeriesVoucherCount'),
      width: 145,
    },
    {
      field: 'voucherRedeemed',
      headerName: t('campaign.campaignSeriesVoucherRedemeed'),
      width: 145,
    },
    {
      field: 'startDate',
      headerName: t('campaign.campaignSeriesStartDate'),
      width: 145,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'endDate',
      headerName: t('campaign.campaignSeriesEndDate'),
      width: 145,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'exported',
      headerName: t('campaign.campaignSeriesExported'),
      headerAlign: 'left',
      width: 135,
      type: 'boolean',
      renderCell: params => <Checkbox checked={params.value} className={classes.checkbox} />,
    },
    {
      field: 'description',
      headerName: t('campaign.campaignSeriesDescription'),
      width: 200,
      flex: 1,
    },
    {
      field: 'id',
      headerName: t('campaign.campaignSeriesActions'),
      width: 230,
      renderCell: params => (
        <Box className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleShowUpdateSeriesDialog(params.row)}
            startIcon={<Icon>edit</Icon>}
            disabled={actionsDisabled}
          >
            {t('common.edit')}
          </Button>
          <Button
            color="primary"
            onClick={() => onDownloadVouchers(params.value)}
            startIcon={<Icon>download</Icon>}
            disabled={actionsDisabled}
          >
            {t('common.download')}
          </Button>
        </Box>
      ),
    },
  ];

  const getRowData = () => series;

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CampaignSeriesTab-showActive"
            color="primary"
            checked={showActive}
            onChange={onFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
      <Button
        id="CampaignSeriesTab-createSeries"
        variant="outlined"
        color="primary"
        startIcon={<Icon>playlist_add</Icon>}
        onClick={handleShowCreateSeriesDialog}
        disabled={actionsDisabled}
      >
        {t('campaign.campaignSeriesCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('campaign.campaignSeriesEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="CampaignSeriesTab-container" className={classes.container}>
      <DataTable
        id="CampaignSeriesTab-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        pageSize={series.length}
        paginationMode="client"
        pagination
      />
    </Box>
  );
}

CampaignSeriesTab.propTypes = {
  campaignId: PropTypes.number.isRequired,
  series: PropTypes.arrayOf(PropTypes.object).isRequired,
  seriesSegments: PropTypes.arrayOf(PropTypes.object).isRequired,
  showActive: PropTypes.bool.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  onCreateSeries: PropTypes.func.isRequired,
  onUpdateSeries: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDownloadVouchers: PropTypes.func.isRequired,
};

export default CampaignSeriesTab;
